/**
 * Per-audience OBJECTIVE — PRESENTATION LAYER ONLY.
 *
 * Turns a published edit into a per-crowd result (landed? how clean?). It reads
 * ONLY the presentation-layer emotion model (`editEmotionVector` / `crowdReaction`)
 * and the publish snapshot's `ScoreBreakdown` (`reach` / `credDelta`). It NEVER
 * feeds `@desk/core` `scoreEdit` / `resolveEdit` / pack validation — every number
 * here is derived, mirroring how the landing/emotion read already works.
 *
 * SINGLE SOURCE OF TRUTH (preview == publish). The editor's live "How it's
 * landing" panel and this objective must score the IDENTICAL vector for the same
 * edit. Both now go through `landingEmotionVector`, which folds in EVERY control
 * that moves the live panel: the scored graph (captions/sound/look/stickers/cut/
 * focus) PLUS the game-side presentation levers that used to be ignored here —
 * stackable EFFECTS (rain/lightning), caption PLACEMENT, reorder/jump-cut Doubt,
 * and the "sloppy cut" leak Doubt. Feed the same `LandingContext` the editor uses
 * and the preview exactly predicts the published result.
 *
 * The tradeoff falls straight out of the crowds' contradictory wants: an honest
 * cut lands the wholesome crowd with low Doubt (no receipts), while landing an
 * outrage crowd usually means pushing checkable claims that raise Doubt (the
 * receipts trail). `clean` marks landing WITHOUT leaving receipts.
 */

import { DEFAULT_CAPTION_ANCHOR_Y_MILLI } from "@desk/core";
import type { ResolvedEdit, ScoreBreakdown, SourcePack } from "@desk/core";
import {
  baseEmotionVector,
  crowdReaction,
  editEmotionVector,
  type CaptionPlacementZone,
  type EditEmotionOptions,
  type EmotionVector,
  type LandingConfig,
} from "./emotion-model.js";
import { buildEditedTimeline, countJumpCuts } from "../shell/lib/timeline.js";
import { placementZoneForAnchorY } from "../shell/lib/caption-placement.js";
import { distinctEffectIds } from "../shell/lib/creative-fx.js";
import type { GamePresentation } from "./challenge/presentation.js";
import type { AudienceResult } from "./types.js";

/**
 * Doubt at or below this reads as CLEAN (no receipts). Tunable, data-driven —
 * kept alongside the landing config's other thresholds. An honest cut lands at
 * ~0 Doubt; a checkable-spin cut lands well above it (see the scenario tests).
 */
export const CLEAN_DOUBT_MAX = 0.15;

/**
 * The game-side presentation levers that move a crowd's landing BEYOND the scored
 * edit graph. These live in editor session / share-link (`GamePresentation`)
 * state, so they arrive here explicitly rather than off `resolved`. Supplying a
 * `pack` opts the read into pack-tick-rate leak Doubt and reorder/jump-cut Doubt
 * (both derived from the resolved edit), exactly as the editor's live panel does.
 */
export interface LandingContext {
  /** Pack for the resolved edit — enables jump-cut + leak (tickRate) Doubt. */
  pack?: SourcePack;
  /** True when the player owns Seamless Transitions (softens jump-cut Doubt). */
  seamlessTransitions?: boolean;
  /** Beats trimmed to their clean end (Precision Trim) — clears their leak Doubt. */
  precisionTrimmedBeatIds?: readonly string[];
  /** Active stackable EFFECT ids (rain / lightning / focus-blur). */
  activeEffectIds?: readonly string[];
  /** The caption's PLACEMENT zone (top / chyron / subject). */
  captionPlacement?: CaptionPlacementZone | null;
}

/**
 * Build the canonical `editEmotionVector` options for a landing read. This is the
 * ONE place the editor preview and the publish objective agree on: it starts from
 * the pack's raw-footage baseline and folds in the presentation levers plus the
 * pack-derived jump-cut / leak Doubt. Callers that omit a `LandingContext` (or its
 * pack) get exactly the old base-only read (effects/placement/jump/leak all inert),
 * so a quick Brief/Compare read is unchanged.
 */
export function landingEmotionOptions(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  landing?: LandingContext,
): EditEmotionOptions {
  const opts: EditEmotionOptions = {
    base: baseEmotionVector(config),
    seamlessTransitions: landing?.seamlessTransitions ?? false,
    precisionTrimmedBeatIds: landing?.precisionTrimmedBeatIds ?? [],
    activeEffectIds: landing?.activeEffectIds ?? [],
  };
  if (landing?.captionPlacement) opts.captionPlacement = landing.captionPlacement;
  if (landing?.pack) {
    opts.tickRate = landing.pack.tickRate;
    opts.jumpCuts = resolved ? countJumpCuts(buildEditedTimeline(landing.pack, resolved)) : 0;
  }
  return opts;
}

/**
 * The emotion vector for a landing read — the SINGLE function the editor's live
 * panel and the publish objective both call, so the preview always equals the
 * published result. Presentation only; never feeds scoring.
 */
export function landingEmotionVector(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  landing?: LandingContext,
): EmotionVector {
  return editEmotionVector(resolved, config, landingEmotionOptions(resolved, config, landing));
}

/**
 * Reconstruct the editor's `LandingContext` from a published edit's game-side
 * presentation sidecar + pack, so a publish (or a reload from a share link)
 * scores the IDENTICAL vector the editor's live panel showed. The caption
 * PLACEMENT zone is derived from the stored caption anchor exactly as the editor
 * derives it (`placementZoneForAnchorY`), defaulting to the caption's lower-third
 * resting position when the player never dragged it. Presentation only.
 */
export function landingContextFromPresentation(
  pack: SourcePack,
  presentation: GamePresentation | undefined,
  opts?: { seamlessTransitions?: boolean },
): LandingContext {
  const captionPos = presentation?.captionPos ?? null;
  return {
    pack,
    seamlessTransitions: opts?.seamlessTransitions ?? false,
    precisionTrimmedBeatIds: presentation?.trims ?? [],
    activeEffectIds: presentation ? distinctEffectIds(presentation.effects) : [],
    captionPlacement: placementZoneForAnchorY(
      captionPos?.yMilli ?? DEFAULT_CAPTION_ANCHOR_Y_MILLI,
    ),
  };
}

/**
 * Compute the per-audience result for a resolved edit against one crowd.
 *
 * The emotion vector is read via `landingEmotionVector`, so it matches EXACTLY
 * what the editor's "How it's landing" shows the player for the same edit +
 * presentation levers. `landed` hits every want without tipping into backlash;
 * `clean` additionally keeps Doubt low. `score` supplies the ground-truth headline
 * (`reach`) and cred penalty (`credDelta`) from the same published edit.
 */
export function computeAudienceResult(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  crowdId: string,
  score: Pick<ScoreBreakdown, "reach" | "credDelta">,
  landing?: LandingContext,
): AudienceResult {
  const crowd = config.crowds[crowdId] ?? config.crowds[config.servedCrowdId]!;
  const served = crowd.id === config.servedCrowdId;
  const vector = landingEmotionVector(resolved, config, landing);
  const reaction = crowdReaction(vector, crowd, served);
  const landed =
    reaction.wantsTotal > 0 && reaction.wantsHit === reaction.wantsTotal && !reaction.inRed;
  const clean = landed && vector.Doubt <= CLEAN_DOUBT_MAX;
  return {
    crowdId: crowd.id,
    landed,
    clean,
    fitMilli: Math.round(reaction.fit * 1000),
    reachMilli: Math.round(score.reach),
    credDeltaMilli: Math.round(score.credDelta),
    doubtMilli: Math.round(vector.Doubt * 1000),
    face: reaction.face,
    status: reaction.status,
  };
}

/**
 * Order two per-audience results by quality (higher = better):
 *   landed > not-landed, then clean > dirty, then higher reach, then higher cred.
 * Returns >0 when `a` is better, <0 when `b` is better, 0 on a tie. Used to keep
 * the BEST result per (story, crowd).
 */
export function compareAudienceResult(a: AudienceResult, b: AudienceResult): number {
  if (a.landed !== b.landed) return a.landed ? 1 : -1;
  if (a.clean !== b.clean) return a.clean ? 1 : -1;
  if (a.reachMilli !== b.reachMilli) return a.reachMilli - b.reachMilli;
  return a.credDeltaMilli - b.credDeltaMilli;
}

/** The better of two results (keeps `prev` on a tie). */
export function bestAudienceResult(
  prev: AudienceResult | undefined,
  next: AudienceResult,
): AudienceResult {
  return !prev || compareAudienceResult(next, prev) > 0 ? next : prev;
}
